import { getActiveRulesByObjectAsync } from '../workflowRuleStorageRouter';
import { workflowRulesStorage } from '../workflowRulesStorage';
import { criteriaEvaluator } from './criteriaEvaluator';
import { actionExecutor } from './actionExecutor';
import { WorkflowRule, WorkflowExecutionLog } from '../../types/workflowRules';

type TriggerType = 'create' | 'update';

/**
 * Main service for executing workflow rules
 */
export class WorkflowExecutionService {

  /**
   * Execute workflows for a record
   * Called when a record is created or updated
   */
  async executeWorkflows(
    objectApiName: string,
    record: any,
    triggerType: TriggerType,
    preloadedRules?: WorkflowRule[]
  ): Promise<void> {
    console.log(`\n🎯 WORKFLOW EXECUTION STARTED`);
    console.log(`   Object: ${objectApiName}`);
    console.log(`   Record: ${record.id}`);
    console.log(`   Trigger: ${triggerType}`);
    console.log(`   Time: ${new Date().toISOString()}`);

    try {
      const activeRules = preloadedRules ?? await getActiveRulesByObjectAsync(objectApiName);

      if (activeRules.length === 0) {
        console.log(`ℹ️  No active workflow rules for ${objectApiName}`);
        return;
      }

      console.log(`📋 Found ${activeRules.length} active rule(s) for ${objectApiName}`);

      // Filter rules by trigger type
      const applicableRules = activeRules.filter(rule =>
        this.shouldTrigger(rule, triggerType)
      );

      if (applicableRules.length === 0) {
        console.log(`ℹ️  No rules match trigger type: ${triggerType}`);
        return;
      }

      console.log(`⚡ ${applicableRules.length} rule(s) match trigger type`);

      // Execute each applicable rule
      for (const rule of applicableRules) {
        await this.executeRule(rule, record, objectApiName);
      }

      console.log(`\n✅ WORKFLOW EXECUTION COMPLETED\n`);
    } catch (error) {
      console.error(`\n❌ WORKFLOW EXECUTION FAILED:`, error);
      throw error;
    }
  }

  /**
   * Check if rule should trigger based on trigger type
   */
  private shouldTrigger(rule: WorkflowRule, triggerType: TriggerType): boolean {
    if (rule.triggerType === 'onCreateOnly' && triggerType === 'create') {
      return true;
    }

    if (rule.triggerType === 'onUpdateOnly' && triggerType === 'update') {
      return true;
    }

    if (rule.triggerType === 'onCreateAndUpdate') {
      return true;
    }

    return false;
  }

  /**
   * Execute a single workflow rule
   */
  private async executeRule(
    rule: WorkflowRule,
    record: any,
    objectApiName: string
  ): Promise<void> {
    console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`🔄 Executing Rule: "${rule.name}"`);
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);

    const startTime = Date.now();

    try {
      // Step 1: Evaluate criteria
      console.log(`\n1️⃣ EVALUATING CRITERIA...`);
      const criteriaMet = criteriaEvaluator.evaluate(rule.evaluationCriteria, record);

      if (!criteriaMet) {
        console.log(`❌ Criteria NOT met - skipping actions`);

        // Log execution (criteria not met)
        this.logExecution(rule, record, objectApiName, false, [], startTime);
        return;
      }

      console.log(`✅ Criteria MET - proceeding with actions`);

      // Step 2: Execute actions
      if (rule.actions.length === 0) {
        console.log(`ℹ️  No actions defined for this rule`);

        // Log execution (no actions)
        this.logExecution(rule, record, objectApiName, true, [], startTime);
        return;
      }

      console.log(`\n2️⃣ EXECUTING ${rule.actions.length} ACTION(S)...`);
      const actionResults = await actionExecutor.executeActions(
        rule.actions,
        record,
        objectApiName
      );

      // Log execution
      this.logExecution(rule, record, objectApiName, true, actionResults, startTime);

      const executionTime = Date.now() - startTime;
      console.log(`\n✅ Rule execution completed (${executionTime}ms)`);
    } catch (error) {
      const executionTime = Date.now() - startTime;
      console.error(`\n❌ Rule execution failed (${executionTime}ms):`, error);

      // Log execution with error
      this.logExecution(
        rule,
        record,
        objectApiName,
        false,
        [],
        startTime,
        [error instanceof Error ? error.message : 'Unknown error']
      );

      throw error;
    }
  }

  /**
   * Log workflow execution
   */
  private logExecution(
    rule: WorkflowRule,
    record: any,
    objectApiName: string,
    criteriaEvaluationResult: boolean,
    actionsExecuted: any[],
    startTime: number,
    errors?: string[]
  ): void {
    const executionTime = Date.now() - startTime;

    const log: Omit<WorkflowExecutionLog, 'id'> = {
      workflowRuleId: rule.id,
      workflowRuleName: rule.name,
      recordId: record.id,
      objectApiName,
      executedAt: new Date().toISOString(),
      executedBy: 'current_user', // TODO: Get from auth context
      criteriaEvaluationResult,
      actionsExecuted,
      executionTimeMs: executionTime,
      errors
    };

    workflowRulesStorage.logExecution(log);

    console.log(`📝 Execution logged`);
  }
}

// Export singleton instance
export const workflowExecutionService = new WorkflowExecutionService();
